import { useState, useEffect } from 'react';
import { variablesTema, cargarFuente } from '../tema';
import { useParams } from 'react-router-dom';
import TicketQR from '../components/TicketQR';
import { money, fmtDate, fullName } from '../events/util';

const API = process.env.REACT_APP_API_URL || '';

export default function BuyerLink() {
  const { code } = useParams();
  const [link, setLink] = useState(null);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);
  const [generated, setGenerated] = useState([]);
  const [form, setForm] = useState({ buyer_name: '', buyer_lastname: '', buyer_city: '', buyer_birthdate: '', buyer_dni: '', buyer_phone: '', buyer_email: '', marketing_consent: false });
  const [ticket, setTicket] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));

  useEffect(() => {
    fetch(`${API}/api/public/link/${code}`)
      .then(async r => {
        const data = await r.json();
        if (r.status === 410) { setExpired(data.error || 'Link no disponible'); setGenerated(data.generated || []); return; }
        if (!r.ok) { setError(data.error || 'Link no encontrado'); return; }
        setLink(data);
        cargarFuente(data && data.tipografia);
      })
      .catch(() => setError('Error de conexión'));
  }, [code]);

  async function submit(e) {
    e.preventDefault();
    if (!form.buyer_name.trim() || !form.buyer_lastname.trim()) { alert('Nombre y apellido obligatorios'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/public/link/${code}/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.status === 410) { setExpired(data.error || 'Link no disponible'); return; }
      if (!res.ok) { alert(data.error || 'No se pudo generar'); return; }
      setTicket(data);
    } catch { alert('Error de conexión'); } finally { setSaving(false); }
  }

  if (error) return <Shell><div style={{ color: 'var(--red-300)', textAlign: 'center' }}>{error}</div></Shell>;
  if (expired) {
    const msg = typeof expired === 'string' ? expired : '';
    const isLot = /lote/i.test(msg);
    const isPaused = /(no disponible|pausa)/i.test(msg);
    const title = isLot ? (isPaused ? 'Lote no disponible' : 'Lote agotado') : (/baja/i.test(msg) ? 'Link dado de baja' : 'Link agotado');
    const sub = isPaused ? 'Este lote está pausado por el momento. Probá más tarde o pedile otro link a tu RRPP.'
      : isLot ? 'Este lote se agotó. Pedile a tu RRPP un link de otro lote.'
      : /baja/i.test(msg) ? 'Este link fue dado de baja. Pedile uno nuevo a tu RRPP.'
      : 'Este link ya no tiene entradas disponibles. Pedile uno nuevo a tu RRPP.';
    return (
      <Shell>
        <div style={{ textAlign: 'center' }}>
          <div className="lb-display" style={{ fontSize: 28, color: 'var(--red-400)' }}>{title}</div>
          <p style={{ color: 'var(--ink-700)', marginTop: 12 }}>{sub}</p>
        </div>
        {generated.length > 0 && (
          <div style={{ marginTop: 28, paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="lb-eyebrow" style={{ textAlign: 'center', marginBottom: 12 }}>
              {generated.length === 1 ? 'Generado por' : 'Generado por'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {generated.map((g, i) => (
                <div key={i} style={{ background: 'var(--ink-100)', border: '1px solid var(--border)', borderRadius: 4, padding: '10px 14px' }}>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{g.name || 'Sin nombre'}</div>
                  <div style={{ color: 'var(--ink-600)', fontSize: 12, marginTop: 2 }}>
                    {[g.dni && `DNI ${g.dni}`, g.email].filter(Boolean).join(' · ')}
                    {g.at ? `${(g.dni || g.email) ? ' · ' : ''}${fmtDate(g.at)}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Shell>
    );
  }
  if (!link) return <Shell><div style={{ color: 'var(--ink-600)', textAlign: 'center' }}>Cargando…</div></Shell>;

  if (ticket) return (
    <Shell logo={link.logo_url} name={link.org_name} tema={variablesTema(link)}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div className="lb-eyebrow">Tu entrada está lista</div>
        <div className="lb-display" style={{ fontSize: 24, marginTop: 6 }}>{ticket.event_name}</div>
        <div style={{ color: 'var(--ink-700)', fontSize: 13, marginTop: 4 }}>{ticket.buyer_name} {ticket.buyer_lastname} · {ticket.lot_name}</div>
        <div style={{ fontFamily: 'monospace', color: 'var(--red-400)', marginTop: 6, letterSpacing: '0.1em' }}>{ticket.qr_code}</div>
      </div>

      {/* Solo traslado: se muestra el QR del MICRO como principal y no el de ingreso.
          El de ingreso existe pero la puerta lo rechaza, así que enseñárselo sería
          prometerle una entrada que no compró. */}
      <TicketQR
        value={ticket.solo_traslado ? ticket.transport_qr : ticket.qr_code}
        transportValue={ticket.solo_traslado ? null : (ticket.transport_qr || null)}
        ingresoLabel={ticket.solo_traslado ? 'TRASLADO' : undefined}
        logo={link.logo_url} eventName={ticket.event_name} date={ticket.date} lotName={ticket.lot_name}
        attendeeName={fullName(ticket.buyer_name, ticket.buyer_lastname)}
        filename={`${ticket.event_name}-${ticket.solo_traslado ? ticket.transport_qr : ticket.qr_code}.png`} />
      <p style={{ color: 'var(--ink-600)', fontSize: 12, textAlign: 'center', marginTop: 20 }}>
        {ticket.solo_traslado
          ? 'Este QR es del TRASLADO, no es la entrada al boliche. Presentalo al subir.'
          : ticket.transport_qr
            ? 'Una sola tarjeta con los dos QR: INGRESO (puerta) y TRASLADO. Cada uno se usa una sola vez.'
            : 'Descargá o sacale captura. Presentalo en la puerta.'}
      </p>
      <Footer text={link.footer_text} />
    </Shell>
  );

  const left = link.max_uses - link.used_count;
  const unitPrice = Number(link.price) || 0;
  // El cupón es de UNA entrada: solo el primero en reclamar del link lo tiene.
  // Del segundo en adelante ven el precio normal, que es lo que el RRPP les va a cobrar.
  const linkDiscount = (!link.coupon_code || Number(link.used_count) > 0) ? 0
    : (link.coupon_type === 'fixed'
        ? Math.min(unitPrice, Number(link.coupon_value) || 0)
        : Math.round(unitPrice * (Number(link.coupon_value) || 0) / 100));
  const linkPrice = Math.max(0, unitPrice - linkDiscount);
  return (
    <Shell logo={link.logo_url} name={link.org_name} tema={variablesTema(link)}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div className="lb-display" style={{ fontSize: 26 }}>{link.event_name}</div>
        <div style={{ color: 'var(--ink-700)', fontSize: 13, marginTop: 6, textTransform: 'capitalize' }}>
          {fmtDate(link.date)}{link.start_time ? ` · ${link.start_time}${link.end_time ? ' — ' + link.end_time : ''}` : ''}
        </div>
        {link.venue && <div style={{ color: 'var(--ink-600)', fontSize: 12, marginTop: 2 }}>{link.venue}{link.city ? ` · ${link.city}` : ''}</div>}
        <div style={{ marginTop: 14, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-700)' }}>Te invita</div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16, color: '#fff' }}>{link.rrpp_name}</div>
      </div>

      <div style={{ background: 'var(--ink-100)', border: '1px solid var(--border)', borderRadius: 4, padding: '16px 20px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, color: '#fff', textTransform: 'uppercase' }}>{link.lot_name}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>{left} {left === 1 ? 'lugar' : 'lugares'} disponible{left === 1 ? '' : 's'}</div>
          {linkDiscount > 0 && <div style={{ fontSize: 11, color: '#2ecc71', marginTop: 4 }}>Cupón {link.coupon_code} aplicado</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          {linkDiscount > 0 && <div style={{ fontSize: 13, color: 'var(--ink-600)', textDecoration: 'line-through' }}>{money(link.price)}</div>}
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22, color: 'var(--red-400)' }}>{money(linkPrice)}</div>
        </div>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <F label="Nombre"><input className="lb-input" value={form.buyer_name} onChange={e => set('buyer_name', e.target.value)} required /></F>
          <F label="Apellido"><input className="lb-input" value={form.buyer_lastname} onChange={e => set('buyer_lastname', e.target.value)} required /></F>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <F label="Localidad"><input className="lb-input" value={form.buyer_city} onChange={e => set('buyer_city', e.target.value)} /></F>
          {/* Fecha de nacimiento en vez de edad: la edad envejece mal (un ticket de hace
              ocho meses con "17" no dice nada sobre esta noche) y con la fecha la puerta
              sabe si puede venderle alcohol el día del evento. */}
          <F label="Fecha de nacimiento"><input className="lb-input" type="date" value={form.buyer_birthdate} onChange={e => set('buyer_birthdate', e.target.value)} /></F>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <F label="DNI"><input className="lb-input" inputMode="numeric" value={form.buyer_dni} onChange={e => set('buyer_dni', e.target.value.replace(/\D/g, ''))} /></F>
          <F label="Teléfono"><input className="lb-input" inputMode="tel" value={form.buyer_phone} onChange={e => set('buyer_phone', e.target.value)} /></F>
        </div>
        <F label="Email (con esto sumás puntos)"><input className="lb-input" type="email" value={form.buyer_email} onChange={e => set('buyer_email', e.target.value)} required /></F>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--ink-700)', lineHeight: 1.45, cursor: 'pointer', marginTop: 4 }}>
          <input type="checkbox" checked={form.marketing_consent} onChange={e => set('marketing_consent', e.target.checked)} style={{ marginTop: 2 }} />
          <span>Quiero recibir novedades y promociones de <b style={{ color: '#fff' }}>{link.org_name || 'este boliche'}</b> por email. <span style={{ color: 'var(--ink-600)' }}>(Podés darte de baja cuando quieras.)</span></span>
        </label>
        <button className="lb-btn lb-btn--primary lb-btn--block" type="submit" disabled={saving} style={{ marginTop: 8 }}>{saving ? 'Generando…' : 'Generar mi QR →'}</button>
      </form>
      <Footer text={link.footer_text} />
    </Shell>
  );
}

function Shell({ children, logo, name, tema }) {
  return (
    <div style={{ ...(tema || {}), minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px' }}>
      <VenueMark logo={logo} name={name} />
      <div style={{ width: '100%', maxWidth: 440 }}>{children}</div>
    </div>
  );
}

function VenueMark({ logo, name, height = 44 }) {
  if (logo) return <img src={logo} alt={name || ''} style={{ height, marginBottom: 28, objectFit: 'contain' }} />;
  if (name) return <div style={{ marginBottom: 28, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, letterSpacing: '0.1em', color: '#fff', textTransform: 'uppercase' }}>{name}</div>;
  return <img src="/landing/tukipro-logo.svg" alt="TUKIPRO" style={{ height: 30, marginBottom: 28, display: 'block' }} />;
}

function F({ label, children }) {
  return <div className="lb-field"><div className="lb-label">{label}</div>{children}</div>;
}

function Footer({ text }) {
  return (
    <div style={{ textAlign: 'center', marginTop: 28, paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-600)' }}>
      {text || 'Acceso reservado · Dress code estricto · +18'}
    </div>
  );
}
